import { useEffect, useState } from "react";

const linkClass = "text-sm text-slate-500 hover:text-slate-900 transition-colors";

// SSG bakes the year into the prerendered HTML at build time. Reading the
// client clock at module load does NOT reproduce it -- after a New Year
// boundary a stale build's HTML says 2026 while the browser says 2027, and
// React throws hydration error #419. Seed from the frozen build-time cutoff
// (the same constant the blog uses) so the first client render is identical to
// the baked HTML, then correct via effect once hydration is done.
declare const __PUBLISH_CUTOFF__: string | undefined;
const INITIAL_YEAR = new Date(
  typeof __PUBLISH_CUTOFF__ === "string" ? __PUBLISH_CUTOFF__ : new Date().toISOString()
).getUTCFullYear();

export function Footer() {
  const [year, setYear] = useState(INITIAL_YEAR);
  useEffect(() => {
    const current = new Date().getFullYear();
    if (current !== INITIAL_YEAR) setYear(current);
  }, []);
  return (
    <footer className="border-t border-slate-100 py-12">
      <div className="max-w-6xl mx-auto px-6 space-y-8">
        {/* Top: logo + link columns */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-8">
          {/* Brand */}
          <div className="col-span-2 sm:col-span-1">
            <a href="/" className="flex items-center gap-2.5">
              <div className="w-7 h-7 bg-indigo-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-xs">O</span>
              </div>
              <span className="font-bold text-sm tracking-tight">
                OmniCanvas
              </span>
            </a>
            <div className="mt-4 flex gap-3">
              <a
                href="https://bsky.app/profile/omnicanvasnotes.bsky.social"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Bluesky"
                className="text-slate-400 hover:text-sky-500 transition-colors"
              >
                <svg width="18" height="18" fill="currentColor" viewBox="0 0 568 501">
                  <path d="M123.121 33.664C188.241 82.553 258.281 181.68 284 234.873c25.719-53.193 95.759-152.32 160.879-201.21C491.866-1.611 568-28.906 568 57.947c0 17.346-9.945 145.713-15.778 166.555-20.275 72.453-94.155 90.933-159.875 79.748C507.222 323.8 536.444 388.56 473.333 453.32c-119.86 122.992-172.272-30.859-185.702-70.281-2.462-7.227-3.614-10.608-3.631-7.733-.017-2.875-1.169.506-3.631 7.733-13.43 39.422-65.842 193.273-185.702 70.281-63.111-64.76-33.89-129.52 80.986-149.071-65.72 11.185-139.6-7.295-159.875-79.748C9.945 203.659 0 75.291 0 57.946 0-28.906 76.135-1.612 123.121 33.664Z" />
                </svg>
              </a>
            </div>
          </div>

          {/* Product */}
          <div className="flex flex-col gap-2">
            <p className="text-sm font-semibold text-slate-900">Product</p>
            <a href="/#features" className={linkClass}>Features</a>
            <a href="/pricing" className={linkClass}>Pricing</a>
            <a href="/tools" className={linkClass}>Free Tools</a>
            <a href="/demo" className={linkClass}>Demo</a>
            <a href="/challenge" className={linkClass}>The Recall Test</a>
            <a href="/meeting-notes" className={linkClass}>AI Meeting Notes</a>
            <a href="/apple-notes-companion" className={linkClass}>Apple Notes Companion</a>
            <a href="/faq" className={linkClass}>FAQ</a>
            <a href="/changelog" className={linkClass}>Changelog</a>
            <a href="https://planetroadmap.com/p/omnicanvas-notes" className={linkClass}>Roadmap</a>
          </div>

          {/* Resources */}
          <div className="flex flex-col gap-2">
            <p className="text-sm font-semibold text-slate-900">Resources</p>
            <a href="/blog" className={linkClass}>Blog</a>
            <a href="/docs/api" className={linkClass}>API Docs</a>
            <a href="/docs/mcp" className={linkClass}>MCP</a>
            <a href="/docs/backups" className={linkClass}>Backups</a>
            <a href="/docs/troubleshooting-stuck-version" className={linkClass}>Stuck Version</a>
            <a href="/docs/troubleshooting-chrome-error-code-5" className={linkClass}>Chrome Error 5</a>
            <a href="https://status.omnicanvasnotes.com" className={linkClass}>Status</a>
            <a href="/press" className={linkClass}>Press</a>
            <a href="/contact" className={linkClass}>Contact</a>
          </div>

          {/* Legal */}
          <div className="flex flex-col gap-2">
            <p className="text-sm font-semibold text-slate-900">Legal</p>
            <a href="/privacy" className={linkClass}>Privacy</a>
            <a href="/terms" className={linkClass}>Terms</a>
            <a href="mailto:hello@omnicanvasnotes.com" className={linkClass}>
              hello@omnicanvasnotes.com
            </a>
          </div>
        </div>

        {/* Bottom: copyright */}
        <div className="pt-6 border-t border-slate-50 text-center">
          <p className="text-sm text-slate-400" suppressHydrationWarning>
            &copy; {year} OmniCanvas. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
}
